import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

interface SurveyResponse {
  id: string
  respondent_name: string
  overall_performance: number
  recommend_likelihood: number
  value_description: string
  technical_accuracy: number
  accuracy_comments: string | null
  completeness: number
  completeness_comments: string | null
  source_links: number
  source_links_comments: string | null
  image_relevance: number | null
  comparison_to_other_tools: string | null
  helpful_in_role: string
  best_use_cases: string | null
  improvement_areas: string
  other_feedback: string | null
  created_at: string
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/uat/survey`
  : '/api/uat/survey'

function ScoreBadge({ value, max = 10 }: { value: number | null; max?: number }) {
  if (value === null) return <span className="text-base-content/40">—</span>
  const pct = value / max
  const color = pct >= 0.8 ? 'badge-success' : pct >= 0.5 ? 'badge-warning' : 'badge-error'
  return <span className={`badge ${color} badge-sm font-bold`}>{value}/{max}</span>
}

export default function UATSurvey() {
  const queryClient = useQueryClient()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: responses = [], isLoading, error } = useQuery<SurveyResponse[]>({
    queryKey: ['uat-survey'],
    queryFn: async () => {
      const res = await fetch(API_BASE)
      if (!res.ok) throw new Error('Failed to fetch survey responses')
      return res.json()
    },
    staleTime: 30000,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uat-survey'] })
    },
  })

  // Compute averages
  const avg = (fn: (r: SurveyResponse) => number) =>
    responses.length > 0
      ? (responses.reduce((s, r) => s + fn(r), 0) / responses.length).toFixed(1)
      : '—'

  const imageResponses = responses.filter((r) => r.image_relevance !== null)
  const imageAvg =
    imageResponses.length > 0
      ? (imageResponses.reduce((s, r) => s + (r.image_relevance ?? 0), 0) / imageResponses.length).toFixed(1)
      : '—'

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">Failed to load survey responses. Is the backend running?</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="page-title">UAT Survey Responses</h2>
        <span className="badge badge-neutral badge-lg">{responses.length} responses</span>
      </div>

      {/* Score Averages */}
      {responses.length > 0 && (
        <div className="stats stats-horizontal shadow w-full bg-base-200">
          <div className="stat">
            <div className="stat-title">Overall</div>
            <div className="stat-value text-2xl">{avg((r) => r.overall_performance)}</div>
            <div className="stat-desc">/ 10</div>
          </div>
          <div className="stat">
            <div className="stat-title">Recommend</div>
            <div className="stat-value text-2xl">{avg((r) => r.recommend_likelihood)}</div>
            <div className="stat-desc">/ 10</div>
          </div>
          <div className="stat">
            <div className="stat-title">Accuracy</div>
            <div className="stat-value text-2xl">{avg((r) => r.technical_accuracy)}</div>
            <div className="stat-desc">/ 10</div>
          </div>
          <div className="stat">
            <div className="stat-title">Completeness</div>
            <div className="stat-value text-2xl">{avg((r) => r.completeness)}</div>
            <div className="stat-desc">/ 10</div>
          </div>
          <div className="stat">
            <div className="stat-title">Source Links</div>
            <div className="stat-value text-2xl">{avg((r) => r.source_links)}</div>
            <div className="stat-desc">/ 10</div>
          </div>
          <div className="stat">
            <div className="stat-title">Images</div>
            <div className="stat-value text-2xl">{imageAvg}</div>
            <div className="stat-desc">/ 10 ({imageResponses.length} rated)</div>
          </div>
        </div>
      )}

      {/* Responses List */}
      {responses.length === 0 ? (
        <div className="alert alert-info">No survey responses yet.</div>
      ) : (
        <div className="space-y-3">
          {responses.map((r) => (
            <div key={r.id} className="card bg-base-200 shadow-sm">
              <div
                className="card-body p-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
              >
                {/* Summary Row */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{r.respondent_name}</h3>
                    <p className="text-sm text-base-content/60">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <ScoreBadge value={r.overall_performance} />
                      <ScoreBadge value={r.technical_accuracy} />
                      <ScoreBadge value={r.completeness} />
                      <ScoreBadge value={r.source_links} />
                    </div>
                    <span className="text-base-content/40">{expandedId === r.id ? '▼' : '▶'}</span>
                  </div>
                </div>

                {/* Value Description */}
                <p className="text-sm mt-1">
                  <span className="font-medium">Value in 3 words:</span> {r.value_description}
                </p>
              </div>

              {/* Expanded Detail */}
              {expandedId === r.id && (
                <div className="border-t border-base-300 p-4 space-y-4 bg-base-100">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Overall Performance</p>
                      <ScoreBadge value={r.overall_performance} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Recommend Likelihood</p>
                      <ScoreBadge value={r.recommend_likelihood} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Technical Accuracy</p>
                      <ScoreBadge value={r.technical_accuracy} />
                      {r.accuracy_comments && (
                        <p className="text-sm mt-1">{r.accuracy_comments}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Completeness</p>
                      <ScoreBadge value={r.completeness} />
                      {r.completeness_comments && (
                        <p className="text-sm mt-1">{r.completeness_comments}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Source Links</p>
                      <ScoreBadge value={r.source_links} />
                      {r.source_links_comments && (
                        <p className="text-sm mt-1">{r.source_links_comments}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Image Relevance</p>
                      <ScoreBadge value={r.image_relevance} />
                    </div>
                  </div>

                  {r.comparison_to_other_tools && (
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Comparison to Other Tools</p>
                      <p className="text-sm mt-1">{r.comparison_to_other_tools}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-base-content/60">Helpful in Role?</p>
                    <p className="text-sm mt-1">{r.helpful_in_role}</p>
                  </div>

                  {r.best_use_cases && (
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Best Use Cases</p>
                      <p className="text-sm mt-1">{r.best_use_cases}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-base-content/60">Needs Improvement</p>
                    <p className="text-sm mt-1">{r.improvement_areas}</p>
                  </div>

                  {r.other_feedback && (
                    <div>
                      <p className="text-sm font-medium text-base-content/60">Other Feedback</p>
                      <p className="text-sm mt-1">{r.other_feedback}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      className="btn btn-error btn-sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm('Delete this survey response?')) {
                          deleteMutation.mutate(r.id)
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
